import type { DurationLike } from 'luxon';
import { DateTime, Duration, Settings } from 'luxon';
import { z } from 'zod';

export type { DateTime } from 'luxon';

type ISO8601Timestamp = string;
type MillisSinceEpoch = number;
export type Timestamp = ISO8601Timestamp | MillisSinceEpoch | Date | DateTime;

export function now(): DateTime {
  return DateTime.now();
}

export function innerParseDateTime(timestamp: Timestamp): DateTime {
  if (timestamp instanceof DateTime) return timestamp;
  if (timestamp instanceof Date) return DateTime.fromJSDate(timestamp);
  if (typeof timestamp === 'number') return DateTime.fromMillis(timestamp);
  const parsedISO = DateTime.fromISO(timestamp);
  if (!parsedISO.isValid) {
    // Attempt parsing common "MMM d, yyyy, h:mm:ss a" format (e.g., "Nov 17, 2025, 6:05:16 PM")
    const fallbackParsed = DateTime.fromFormat(timestamp, 'LLL d, yyyy, h:mm:ss a');
    if (fallbackParsed.isValid) return fallbackParsed;
  }
  return parsedISO;
}

/**
 * Helper method to create a DateTime luxon object.
 * If you pass a luxon DateTime, it's just pass-through.
 * Other supported formats are numbers for milliseconds since unix epoch and iso 8601 timestamps.
 * @returns null if the timestamp is invalid.
 */
export function parseDateTime(timestamp: Timestamp | null | undefined): DateTime | null {
  if (!timestamp) return null;
  const dt = innerParseDateTime(timestamp);
  if (!dt.isValid) return null;
  return dt;
}

export enum TimeFormat {
  LONG = 'long',
  LONG_WITH_MS = 'long-with-ms',
  MEDIUM = 'medium',
  SHORT = 'short',
  TIME = 'time',
  TIME_SECONDS = 'time-seconds',
  '24_HOUR_MINUTES' = '24-hour-minutes',
  RELATIVE = 'relative',
  U_NAMED_OFFSET = 'uNamedOffset',
  INPUT_DATETIME = 'input-datetime',
}

export type TimeFormatValue = `${TimeFormat}`;

export enum HourCyclePreference {
  Automatic = 'automatic',
  TwelveHour = '12-hour',
  TwentyFourHour = '24-hour',
}

export const hourCyclePreferenceSchema = z.nativeEnum(HourCyclePreference);

const SYSTEM_DEFAULT_LOCALE = Settings.defaultLocale;

/**
 * Switches every locale-aware Luxon output (formatDateTime, toLocaleString, the `t` macro token)
 * between 12- and 24-hour time via a `-u-hc-` extension on Luxon's default locale.
 */
export function applyHourCyclePreference(preference: HourCyclePreference): void {
  if (preference === HourCyclePreference.Automatic) {
    Settings.defaultLocale = SYSTEM_DEFAULT_LOCALE;
    return;
  }
  const systemLocale = new Intl.DateTimeFormat().resolvedOptions().locale;
  Settings.defaultLocale = new Intl.Locale(systemLocale, {
    hourCycle: preference === HourCyclePreference.TwelveHour ? 'h12' : 'h23',
  }).toString();
}

const INVALID_DATE_TIME = 'Invalid DateTime';

function assertUnreachable(value: never): never {
  throw new Error(`Unhandled time format: ${value}`);
}

/**
 * Given a date in varying formats, returns a string representation.
 *
 * @param timestamp - The timestamp to format (ISO string, ms, Date, or DateTime)
 * @param format - The display format
 * @param zone - IANA timezone (e.g. 'America/New_York', 'utc', 'local')
 *
 * @example
 * formatDateTime(date, TimeFormat.LONG)               // "1/8/2026, 10:49:39 AM"
 * formatDateTime(date, TimeFormat.LONG_WITH_MS)       // "1/8/2026, 10:49:39.123 AM"
 * formatDateTime(date, TimeFormat.MEDIUM)             // "1/8/2026, 10:49 AM"
 * formatDateTime(date, TimeFormat.SHORT)              // "1/8/2026"
 * formatDateTime(date, TimeFormat.TIME_SECONDS)       // "10:49:39 AM"
 * formatDateTime(date, TimeFormat.TIME)               // "10:49 AM"
 * formatDateTime(date, TimeFormat.RELATIVE)           // "10 hours ago"
 * formatDateTime(date, TimeFormat.U_NAMED_OFFSET)     // "PST"
 * formatDateTime(date, TimeFormat['24_HOUR_MINUTES']) // "10:49"
 *
 */
export function formatDateTime(
  timestamp: Timestamp,
  format: TimeFormat | TimeFormatValue = TimeFormat.MEDIUM,
  zone?: string,
): string {
  let dateTime = parseDateTime(timestamp);
  if (!dateTime) return INVALID_DATE_TIME;

  if (zone) {
    dateTime = dateTime.setZone(zone);
  }

  const tf = format as TimeFormat;

  switch (tf) {
    case TimeFormat.LONG:
      return dateTime.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
    case TimeFormat.LONG_WITH_MS:
      return dateTime.toLocaleString({
        ...DateTime.DATETIME_SHORT_WITH_SECONDS,
        fractionalSecondDigits: 3,
      });
    case TimeFormat.MEDIUM:
      return dateTime.toLocaleString(DateTime.DATETIME_SHORT);
    case TimeFormat.SHORT:
      return dateTime.toLocaleString(DateTime.DATE_SHORT);
    case TimeFormat.TIME:
      return dateTime.toLocaleString(DateTime.TIME_SIMPLE);
    case TimeFormat.TIME_SECONDS:
      return dateTime.toLocaleString(DateTime.TIME_WITH_SECONDS);
    case TimeFormat.RELATIVE: {
      const diff = now().diff(dateTime, 'seconds');
      if (Math.abs(diff.seconds) < 10) {
        return 'Just now';
      }
      return dateTime.toRelative() ?? INVALID_DATE_TIME;
    }
    case TimeFormat.U_NAMED_OFFSET:
      return dateTime.toFormat('ZZZZ');
    case TimeFormat['24_HOUR_MINUTES']:
      return dateTime.toFormat('HH:mm');
    case TimeFormat.INPUT_DATETIME:
      return dateTime.toFormat("yyyy-MM-dd'T'HH:mm");
    default:
      // Force this switch statement to be exhaustive
      return assertUnreachable(tf);
  }
}

/** Formats an optional timestamp, omitting missing or invalid values. */
export function formatOptionalDateTime(
  timestamp: Timestamp | null | undefined,
  format: TimeFormat | TimeFormatValue = TimeFormat.MEDIUM,
  zone?: string,
): string | undefined {
  const dateTime = parseDateTime(timestamp);
  return dateTime ? formatDateTime(dateTime, format, zone) : undefined;
}

/** Formats a range, omitting the repeated date when both endpoints fall on the same day. */
export function formatDateTimeRange(start: Timestamp, end: Timestamp, zone?: string): string {
  const parsedStart = parseDateTime(start);
  const parsedEnd = parseDateTime(end);
  if (!parsedStart || !parsedEnd) return INVALID_DATE_TIME;

  const startAt = zone ? parsedStart.setZone(zone) : parsedStart;
  const endAt = zone ? parsedEnd.setZone(zone) : parsedEnd;
  const startLabel = formatDateTime(startAt, TimeFormat.MEDIUM);
  const endLabel = formatDateTime(
    endAt,
    startAt.hasSame(endAt, 'day') ? TimeFormat.TIME : TimeFormat.MEDIUM,
  );
  return `${startLabel} – ${endLabel} ${formatDateTime(startAt, TimeFormat.U_NAMED_OFFSET)}`;
}

/**
 * Labels the whole-calendar-day distance from today to a target date:
 * "today", "in 3 days", "2 days overdue". The target is a calendar date
 * (midnight-UTC instant or bare YYYY-MM-DD — both read as a UTC day),
 * compared against today in the given zone (the viewer's local zone by default).
 */
export function daysToTargetLabel(target: string, zone = 'local'): string {
  const targetDay = DateTime.fromISO(target, { zone: 'utc' }).startOf('day');
  const current = now().setZone(zone);
  if (!targetDay.isValid || !current.isValid) return INVALID_DATE_TIME;
  const today = DateTime.utc(current.year, current.month, current.day);
  const { days } = targetDay.diff(today, 'days');
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
  return `${-days} day${days === -1 ? '' : 's'} overdue`;
}

/** Timezone abbreviation in the browser's local zone at this instant (e.g. PDT, EST). */
export function formatLocalTimezoneAbbreviation(instant: Timestamp = DateTime.now()): string {
  return formatDateTime(instant, TimeFormat.U_NAMED_OFFSET, 'local');
}

/** Table column header with browser-local timezone abbrev, e.g. `Scheduled (PDT)`. */
export function tableColumnHeaderWithLocalTimezoneAbbreviation(
  columnLabel: string,
  instant: Timestamp = DateTime.now(),
): string {
  return `${columnLabel} (${formatLocalTimezoneAbbreviation(instant)})`;
}

export type DurationUnitDisplay = 'short' | 'long' | 'narrow';

/**
 * Given a Duration, returns a string representation.
 *
 * @param duration - The duration to format
 * @param unitDisplay - The display format
 * @param maxUnits - The max number of non-zero units to display.
 *
 * @example
 * durationToHuman({ minutes: 10, seconds: 5 })                         // "10 min, 5 sec"
 * durationToHuman({ minutes: 10, seconds: 5 }, undefined, 1)           // "10 min"
 * durationToHuman({ hours: 2, minutes: 0, seconds: 5 }, 'narrow', 2)   // "2h, 5s"
 *
 */
export function durationToHuman(
  duration: DurationLike,
  unitDisplay?: DurationUnitDisplay,
  maxUnits?: number,
): string {
  let rescaled = Duration.fromDurationLike(duration).rescale();
  if (maxUnits != null && maxUnits > 0) {
    const entries = Object.entries(rescaled.toObject()).filter(([, v]) => v !== 0);
    if (entries.length > maxUnits) {
      rescaled = Duration.fromObject(Object.fromEntries(entries.slice(0, maxUnits)));
    }
  }
  return rescaled.toHuman({ unitDisplay }) || '0 seconds';
}

/**
 * Given a number of seconds, returns a string representation.
 *
 * @param seconds - The number of seconds to format
 * @param unitDisplay - The display format
 * @param maxUnits - The max number of non-zero units to display.
 *
 * @example
 * secToHuman(87005)                // "1 day, 0 hrs, 10 min, 5 sec"
 * secToHuman(87005, 'long', 2)     // "1 day, 10 minutes"
 *
 */
export function secToHuman(
  seconds: number,
  unitDisplay?: DurationUnitDisplay,
  maxUnits?: number,
): string {
  return durationToHuman({ seconds }, unitDisplay, maxUnits);
}

export function stripDateForInput(dt: DateTime): string {
  return dt.toISODate() ?? 'Invalid date';
}

export function stripDateTimeForInput(dt: DateTime): string {
  return (
    dt
      .set({
        second: 0,
        millisecond: 0,
      })
      .toISO({
        includeOffset: false,
        suppressSeconds: true,
        suppressMilliseconds: true,
      }) ?? 'Invalid date time'
  );
}

// TODO: rename w/ more semantic context
export function maybeParseDate(maybeDate: Date | string): Date {
  if (maybeDate instanceof Date) return maybeDate;
  return DateTime.fromISO(maybeDate).toJSDate();
}

export enum DurationUnit {
  Years = 'years',
  Months = 'months',
  Weeks = 'weeks',
  Days = 'days',
  Hours = 'hours',
  Minutes = 'minutes',
  Seconds = 'seconds',
  Milliseconds = 'milliseconds',
}

const DURATION_UNIT_VALUE_MESSAGE = 'Please provide a positive integer.';
const messageObject = { message: DURATION_UNIT_VALUE_MESSAGE };
const optionalNonnegativeInteger = z
  .number()
  .int(messageObject)
  .nonnegative(messageObject)
  .optional();

export const possiblyEmptyDurationSchema = z
  .object({
    years: optionalNonnegativeInteger,
    months: optionalNonnegativeInteger,
    weeks: optionalNonnegativeInteger,
    days: optionalNonnegativeInteger,
    hours: optionalNonnegativeInteger,
    minutes: optionalNonnegativeInteger,
    seconds: optionalNonnegativeInteger,
    milliseconds: optionalNonnegativeInteger,
  })
  .refine((obj) => Duration.fromDurationLike(obj).isValid, {
    message: 'Please provide a valid duration.',
  });

export const durationSchema = possiblyEmptyDurationSchema.refine(
  (obj) => Object.values(obj).some((v) => v != null && v !== 0),
  {
    message: 'Please provide at least one positive value of duration measurement.',
  },
);

export type DurationObject = z.infer<typeof durationSchema>;
